import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Spinner,
  Input,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Badge,
  Button,
  Label,
  Row,
  Col,
} from 'reactstrap';
import { DateRangePicker } from 'rsuite';
import moment from 'moment';
import toast from 'react-hot-toast';
import {
  exportServiceOrders,
  getTechniciansList,
} from '../actions/action';

const SERVICE_TYPE_OPTIONS = [
  'filter_change',
  'water_leakage',
  'installation',
  'reinstallation',
  'amc',
  'others',
];

const STATUS_OPTIONS = [
  { value: '1', label: 'Pending' },
  { value: '11', label: 'New' },
  { value: '12', label: 'Assigned To Technician' },
  { value: '14', label: 'In Progress' },
  { value: '15', label: 'Resolved' },
  { value: '2', label: 'Canceled' },
  { value: '3', label: 'Closed' },
  { value: '13', label: 'Rescheduled' },
];

const EMIRATE_OPTIONS = [
  'Dubai',
  'Abu Dhabi',
  'Sharjah',
  'Ajman',
  'Fujairah',
  'Ras Al Khaimah',
  'Umm Al Quwain',
];

function ServiceOrderExportReport() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const { afterToday, combine, allowedMaxDays } = DateRangePicker;

  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [reportType, setReportType] = useState('normal');
  const [status, setStatus] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [emirate, setEmirate] = useState('');
  const [technicianId, setTechnicianId] = useState('');

  // Drafts so the user can cancel without losing the committed filters.
  const [draftStatus, setDraftStatus] = useState('');
  const [draftServiceType, setDraftServiceType] = useState('');
  const [draftEmirate, setDraftEmirate] = useState('');
  const [draftTechnicianId, setDraftTechnicianId] = useState('');

  const currentCountry = localStorage.getItem('WEBSITE_COUNTRY') || 'ae';

  const { techniciansList } = useSelector((state) => state.technician);
  const technicians = techniciansList?.technicians || [];

  useEffect(() => {
    getTechniciansList({ dispatch });
  }, [dispatch]);

  const activeFilterCount = useMemo(
    () =>
      [status, serviceType, emirate, technicianId].filter(
        (v) => v && String(v).length > 0,
      ).length,
    [status, serviceType, emirate, technicianId],
  );

  const openFilterDrawer = () => {
    // Seed drafts from the committed values so the drawer opens with the
    // user's existing selections visible.
    setDraftStatus(status);
    setDraftServiceType(serviceType);
    setDraftEmirate(emirate);
    setDraftTechnicianId(technicianId);
    setIsFilterDrawerOpen(true);
  };

  const closeFilterDrawer = () => setIsFilterDrawerOpen(false);

  const handleApplyFilters = () => {
    setStatus(draftStatus);
    setServiceType(draftServiceType);
    setEmirate(draftEmirate);
    setTechnicianId(draftTechnicianId);
    setIsFilterDrawerOpen(false);
  };

  const handleResetFilters = () => {
    setDraftStatus('');
    setDraftServiceType('');
    setDraftEmirate('');
    setDraftTechnicianId('');
    setStatus('');
    setServiceType('');
    setEmirate('');
    setTechnicianId('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!startDate || !endDate) {
      toast.error('Please select a date range');
      return;
    }
    setLoading(true);
    try {
      const result = await exportServiceOrders({
        params: {
          date_from: startDate,
          date_to: endDate || startDate,
          country: currentCountry,
          report_type: reportType,
          status,
          service_type: serviceType,
          emirate,
          technician_id: technicianId,
        },
        dispatch,
      });
      if (result?.status === 'success') {
        toast.success(
          result?.message || 'Report will be emailed to you when ready.',
        );
      } else {
        toast.error(result?.message || 'Export failed. Please try again.');
      }
    } catch (error) {
      toast.error('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="service-export-container">
      <Form onSubmit={handleSubmit}>
        <div className="DashbordFliter d-flex flex-wrap justify-content-end align-items-center gap-2">
          <div style={{ minWidth: 160 }}>
            <Input
              type="select"
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              disabled={loading}
              title="Report Type"
            >
              <option value="normal">Normal Report</option>
              <option value="accounts">Accounts Report</option>
            </Input>
          </div>

          <div className="InputBg">
            <DateRangePicker
              placement="leftStart"
              placeholder="Select Date Range"
              onClean={() => {
                setStartDate(moment().format('YYYY-MM-DD'));
                setEndDate(moment().format('YYYY-MM-DD'));
              }}
              disabledDate={combine(allowedMaxDays(365), afterToday())}
              onChange={(selectedDates) => {
                if (selectedDates && selectedDates.length === 2) {
                  setStartDate(
                    moment(selectedDates[0]).format('YYYY-MM-DD'),
                  );
                  setEndDate(moment(selectedDates[1]).format('YYYY-MM-DD'));
                }
              }}
            />
          </div>

          <button
            type="button"
            className="btn btn-outline-dark d-inline-flex align-items-center gap-1"
            onClick={openFilterDrawer}
            disabled={loading}
          >
            <i className="bx bx-filter-alt"></i>
            Filters
            {activeFilterCount > 0 && (
              <Badge color="dark" pill className="ms-1">
                {activeFilterCount}
              </Badge>
            )}
          </button>

          <button
            type="submit"
            className="btn btn-dark d-flex align-items-center"
            disabled={loading}
          >
            {loading && (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
            )}
            Export
            <i
              className="bx bx-download ms-2"
              style={{ fontSize: '18px' }}
            ></i>
          </button>
        </div>
      </Form>

      <Offcanvas
        isOpen={isFilterDrawerOpen}
        toggle={closeFilterDrawer}
        direction="end"
        style={{ width: '420px' }}
      >
        <OffcanvasHeader toggle={closeFilterDrawer} className="border-bottom">
          Export Filters
        </OffcanvasHeader>
        <OffcanvasBody>
          <Row>
            <Col md={12} className="mb-3">
              <Label className="form-label fw-semibold">Status</Label>
              <Input
                type="select"
                value={draftStatus}
                onChange={(e) => setDraftStatus(e.target.value)}
              >
                <option value="">All Status</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={12} className="mb-3">
              <Label className="form-label fw-semibold">Service Type</Label>
              <Input
                type="select"
                value={draftServiceType}
                onChange={(e) => setDraftServiceType(e.target.value)}
                className="text-capitalize"
              >
                <option value="">All Service Types</option>
                {SERVICE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt} className="text-capitalize">
                    {opt.replace(/_/g, ' ')}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={12} className="mb-3">
              <Label className="form-label fw-semibold">Emirate</Label>
              <Input
                type="select"
                value={draftEmirate}
                onChange={(e) => setDraftEmirate(e.target.value)}
              >
                <option value="">All Emirates</option>
                {EMIRATE_OPTIONS.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={12} className="mb-3">
              <Label className="form-label fw-semibold">Technician</Label>
              <Input
                type="select"
                value={draftTechnicianId}
                onChange={(e) => setDraftTechnicianId(e.target.value)}
              >
                <option value="">All Technicians</option>
                {technicians.map((t) => (
                  <option key={t.emp_id} value={t.emp_id}>
                    {`${t.technician_name || ''}${
                      t.emp_id ? ` (${t.emp_id})` : ''
                    }`.trim()}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-4">
            <Button color="success" onClick={handleApplyFilters}>
              Apply
            </Button>
            <Button outline color="danger" onClick={handleResetFilters}>
              Reset
            </Button>
          </div>
        </OffcanvasBody>
      </Offcanvas>
    </div>
  );
}

export default ServiceOrderExportReport;
